/*
 * <fz-home-rail>: the homepage's product rows (snippets/fz-home-cards), one
 * row that scrolls sideways at every width. The swipe and the trackpad are the
 * browser's own (CSS scroll-snap); this adds the previous/next arrows a mouse
 * and a keyboard need, the video carousel's pattern (assets/fz-video.js):
 *
 * - A press steps a SCREEN of whole cards, never a fixed pixel count, so a
 *   343 px card and a 221 px one both land on a card's edge.
 * - The arrows are disabled at each end. Two sentinels, the first and the last
 *   card, say so through an IntersectionObserver rooted on the row, so there is
 *   no scroll listener and nothing measures layout while the row moves. A row
 *   inside a hidden tab panel reports again when its panel is shown.
 * - The arrows are in the markup and the layout before this runs (disabled
 *   "previous" included), so its arrival shifts nothing.
 *
 * INP: a press does one getBoundingClientRect and one scrollBy.
 */

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

class FzHomeRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-fz-track]");
    this.prev = this.querySelector("[data-fz-prev]");
    this.next = this.querySelector("[data-fz-next]");
    if (!this.track || !this.prev || !this.next) return;

    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    this.prev.addEventListener("click", this.onPrev);
    this.next.addEventListener("click", this.onNext);

    const first = this.track.firstElementChild;
    const last = this.track.lastElementChild;
    if (!first || !last) return;
    this.edges = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const button = entry.target === first ? this.prev : this.next;
          // Never leave focus on a button that has just become disabled: hand
          // it to the other arrow, which can still move the row.
          const disable = entry.intersectionRatio > 0.9;
          if (disable && document.activeElement === button) {
            (button === this.prev ? this.next : this.prev).focus({ preventScroll: true });
          }
          button.disabled = disable;
        }
      },
      { root: this.track, threshold: [0, 0.9, 1] },
    );
    this.edges.observe(first);
    this.edges.observe(last);
  }

  disconnectedCallback() {
    this.edges?.disconnect();
    this.prev?.removeEventListener("click", this.onPrev);
    this.next?.removeEventListener("click", this.onNext);
  }

  step(direction) {
    const card = this.track.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const width = card.getBoundingClientRect().width + gap;
    const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / width));
    const rtl = getComputedStyle(this).direction === "rtl" ? -1 : 1;
    this.track.scrollBy({
      left: direction * rtl * width * visible,
      behavior: reduceMotion.matches ? "auto" : "smooth",
    });
  }
}

if (!customElements.get("fz-home-rail")) customElements.define("fz-home-rail", FzHomeRail);
